import { cssVars } from '@/app/styles';
import { Charts } from '@/chart/chart';
import { LineChartOptions } from '@/chart/chart-options';
import { DEFAULT_CHART_BASE_TYPE } from '@/chart/components/all-charts-styles';
import { DisplayMinMaxValuePlugin } from '@/chart/plugins/display-min-max-value-plugin';
import { datasetTypes } from '@/constants';
import {
  ChartDataBuilder,
  constructDatasets,
} from '@/utils/chart-data-builder';
import { numberToPercent } from '@/utils/string-utils';

export class OnlinePlatformsDataBuilder extends ChartDataBuilder {
  constructor(data, chartMode = null, dataType = 'onlinePlatforms') {
    super(data);
    this.dataType = dataType;
    this.chartMode = chartMode;
    this.datasetConfigurations = this.chartDatasetConfigs;
    this.data = this.extractOnlinePlatforms(data);
  }

  get chartDatasetConfigs() {
    const order = {
      FIRST: 0,
      SECOND: 1,
      THIRD: 2,
    };
    // Order Enum
    const labelOrder = [order.FIRST, order.SECOND, order.THIRD];
    const LABEL = 0;
    const DATA = 1;
    const data = this.chartData;
    const { onlinePlatforms } = data.latestKpi;
    const sortedPlatforms = Object.entries(onlinePlatforms).sort(
      (a, b) => b[DATA].value - a[DATA].value
    );

    // Add Ordered Configs
    const datasetConfigs = labelOrder.map((position) => ({
      fill: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderColor:
        cssVars.color.platformColor[sortedPlatforms[position][LABEL]]?.dark ||
        cssVars.color.platformColor.others.dark,
      backgroundColor:
        cssVars.color.platformColor[sortedPlatforms[position][LABEL]]?.dark ||
        cssVars.color.platformColor.others.dark,
    }));

    // add "Other" Configs
    datasetConfigs.push({
      fill: false,
      pointRadius: 2,
      pointHoverRadius: 4,
      borderWidth: 2,
      borderDash: [6, 5],
      borderColor: cssVars.color.platformColor.others.dark,
      backgroundColor: cssVars.color.platformColor.others.dark,
    });
    return datasetConfigs;
  }

  static get configurations() {
    return this.chartDatasetConfigs;
  }

  extractOnlinePlatforms = ({ dataPoints }) => {
    const LABEL = 0; // Represents the subtype from online platforms
    const FIRST = 0; // First element in the list
    const DATA = 1; // Position where Data of subtype can be found
    const TOP_THREE = 3; // First three are extracted differently than last one
    const lastPoint = dataPoints[dataPoints.length - 1].onlinePlatforms;
    const datasets = [];
    // Determines order since there will only be 4 datasets (includes Others)
    const sortedPlatforms = Object.entries(lastPoint).sort(
      (a, b) => b[DATA].value - a[DATA].value
    );
    // This will create 3 datasets of the highest data
    for (let i = 0; i < TOP_THREE; i++) {
      const data = [];
      dataPoints.forEach((point) => {
        data.push(point.onlinePlatforms[sortedPlatforms[FIRST][LABEL]].value);
      });
      // Consumed, remove from the platforms list, this helps to aggregate remaining
      sortedPlatforms.shift();
      datasets.push(data);
    }

    const othersData = [];
    // This will create one dataset with others aggregated
    dataPoints.forEach((point) => {
      let otherSum = 0;
      sortedPlatforms.forEach((platform) => {
        otherSum += point.onlinePlatforms[platform[LABEL]].value;
      });
      othersData.push(otherSum);
    });
    datasets.push(othersData);
    return datasets;
  };

  get retrieveDatasets() {
    return constructDatasets(
      this.labels,
      this.data,
      'line',
      this.datasetConfigurations
    );
  }
}

class OnlinePlatformsChart extends Charts {
  static pluginParams = {
    pluginTextColor: cssVars.chartColors.ONLINE_PLATFORMS,
    pluginFormatter: numberToPercent,
    pluginAlignment: 'top',
  };

  static tooltipParams = {
    unit: '',
  };

  static getParams() {
    return {
      pluginParams: this.pluginParams,
      tooltipParams: this.tooltipParams,
    };
  }

  static getOptions(props) {
    return {
      ...OnlinePlatformsChart.getParams(),
      ...props,
    };
  }

  constructor(props) {
    super({
      DataBuilder: OnlinePlatformsDataBuilder,
      chartProps: props,
      name: datasetTypes.ONLINE_PLATFORMS,
      options: new LineChartOptions(OnlinePlatformsChart.getOptions(props))
        .chartOptions,
      baseType: DEFAULT_CHART_BASE_TYPE,
      plugins: [DisplayMinMaxValuePlugin()],
    });
  }
}

const chart = (props) => new OnlinePlatformsChart(props).chart;

export default chart;
